// F7: "Send feedback", for anyone (the review pins need a secret ?review link). Self-contained: drop
// <SendFeedback /> anywhere; it renders its own trigger and sheet. The note goes to the `review` function's public
// 'feedback' action (stored in review_comments, key_id 'feedback', no user id); Claude reads them with
// `python3 ops/review_comments.py --feedback pull`. Context sent automatically: the path (never the query), the app
// version, the screen size; the server adds the browser string. No screenshot.
import { useState } from 'react'
import { callFn, ApiError } from '../lib/api'
import { fb, type FbKey } from '../copyFeedback'
import { Sheet } from '../ui/Sheet'
import { Button } from '../ui/Button'
import { ToggleChip } from '../ui/Chip'
import { Spinner } from '../ui/Spinner'

const DEPLOY = (import.meta.env.VITE_RELEASE as string | undefined) ?? 'dev'
const MAX = 4000
const DOING: { value: string; key: FbKey }[] = [
  { value: 'setup', key: 'fb.doing_setup' },
  { value: 'browsing', key: 'fb.doing_browsing' },
  { value: 'planning', key: 'fb.doing_planning' },
  { value: 'calendar', key: 'fb.doing_calendar' },
  { value: 'other', key: 'fb.doing_other' },
]

type Props = {
  /** 'link': a quiet text link (first run, Browse). 'row': a full-width row like Settings' other rows. */
  variant?: 'link' | 'row'
  /** Preselected chip for where it sits (first run -> 'setup'); the person can change or clear it. */
  doing?: string
  className?: string
}

export function SendFeedback({ variant = 'link', doing: preset, className = '' }: Props) {
  const [open, setOpen] = useState(false)
  const [text, setText] = useState('')
  const [doing, setDoing] = useState<string | null>(preset ?? null)
  const [state, setState] = useState<'idle' | 'sending' | 'sent'>('idle')
  const [err, setErr] = useState<FbKey | null>(null)

  const show = () => { setOpen(true); setErr(null); if (state === 'sent') { setState('idle'); setText(''); setDoing(preset ?? null) } }
  const close = () => { if (state !== 'sending') setOpen(false) }

  const send = async () => {
    const message = text.trim()
    if (!message || state === 'sending') return
    if (typeof navigator !== 'undefined' && navigator.onLine === false) { setErr('fb.offline'); return }
    setState('sending'); setErr(null)
    try {
      await callFn('review', {
        action: 'feedback',
        note: { message, doing, route: location.pathname, viewport: { w: innerWidth, h: innerHeight, dpr: devicePixelRatio }, deploy_id: DEPLOY },
      })
      setState('sent')
    } catch (e) {
      setState('idle')
      const status = e instanceof ApiError ? e.status : null
      setErr(status === 429 ? 'fb.too_many' : navigator.onLine === false || status === null ? 'fb.offline' : 'fb.error')
    }
  }

  const trigger = variant === 'row' ? (
    <button type="button" onClick={show} data-review-id="send-feedback"
      className={`press flex min-h-[52px] w-full items-center justify-between rounded-2xl bg-surface px-4 text-left text-[17px] text-ink ring-1 ring-inset ring-line ${className}`}>
      {fb('fb.link')}
    </button>
  ) : (
    <button type="button" onClick={show} data-review-id="send-feedback"
      className={`press t-meta inline-flex min-h-11 items-center text-ink-2 underline underline-offset-4 ${className}`}>
      {fb('fb.link')}
    </button>
  )

  return (
    <>
      {trigger}
      <Sheet open={open} onClose={close} locked={state === 'sending'} label={fb('fb.title')} reviewState="feedback">
        {state === 'sent' ? (
          <div data-review-id="feedback-sent" role="status">
            <h2 className="t-control pr-10 text-[19px] leading-6 text-ink">{fb('fb.sent_title')}</h2>
            <p className="mt-1.5 text-[15px] text-ink-2">{fb('fb.sent_body')}</p>
            <Button block className="mt-5" onClick={() => setOpen(false)}>{fb('fb.done')}</Button>
          </div>
        ) : (
          <form data-review-id="feedback-sheet" onSubmit={(e) => { e.preventDefault(); void send() }}>
            <h2 className="t-control pr-10 text-[19px] leading-6 text-ink">{fb('fb.title')}</h2>
            <label htmlFor="feedback-text" className="mt-3 block text-[15px] font-semibold text-ink">{fb('fb.label')}</label>
            <textarea id="feedback-text" value={text} maxLength={MAX} rows={4} onChange={(e) => setText(e.target.value)}
              placeholder={fb('fb.placeholder')}
              className="mt-2 block w-full resize-none border border-boundary bg-surface p-3 text-[16px] text-ink placeholder:text-ink-3 focus:border-accent focus:outline-none" />
            <p className="mt-4 text-[13px] font-semibold text-ink-2" id="feedback-doing">{fb('fb.doing')}</p>
            <div role="group" aria-labelledby="feedback-doing" className="mt-2 flex flex-wrap gap-2">
              {DOING.map((d) => (
                <ToggleChip key={d.value} on={doing === d.value} onToggle={() => setDoing(doing === d.value ? null : d.value)}>{fb(d.key)}</ToggleChip>
              ))}
            </div>
            <p className="t-caption mt-4 text-ink-2">{fb('fb.context')}</p>
            {err && <p role="alert" className="t-meta mt-3 text-error">{fb(err)}</p>}
            <Button type="submit" block className="mt-4" disabled={!text.trim() || state === 'sending'}>
              {state === 'sending' ? <><Spinner size={18} label={false} className="text-current" />{fb('fb.sending')}</> : fb('fb.send')}
            </Button>
          </form>
        )}
      </Sheet>
    </>
  )
}
